import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";

export const Route = createFileRoute("/warranty/")({
  head: () => ({
    meta: [
      { title: "Warranty — Double Lion" },
      { name: "description", content: "Every Double Lion instrument is covered by a 5-year international warranty. Learn what's covered and how to submit a claim." },
      { property: "og:title", content: "Warranty — Double Lion" },
      { property: "og:description", content: "5-year international warranty on every Double Lion instrument." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://double-lion-muse.lovable.app/warranty" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      { rel: "canonical", href: "https://double-lion-muse.lovable.app/warranty" },
    ],
  }),
  component: WarrantyPage,
});

const covered = [
  "Optical defects in prisms, lenses, and coatings",
  "Mechanical failure of focus, diopter, or hinge systems",
  
  "Defects in materials or assembly under normal use",
];

const notCovered = [
  "Cosmetic wear consistent with everyday use",
  "Damage from impact, drops, or misuse",
  "Unauthorized service, modification, or disassembly",
  "Loss, theft, or accessories sold separately",
];

const steps = [
  { n: "01", title: "Locate your serial", body: "Engraved on the underside of the right barrel, near the strap lug." },
  { n: "02", title: "Document the issue", body: "Take 2–4 clear photos showing the defect or area of concern." },
  { n: "03", title: "Submit a claim", body: "We respond within 3 business days with next steps and a return label if needed." },
];

function WarrantyPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main className="pt-32 pb-32">
        {/* Hero */}
        <section className="mx-auto max-w-5xl px-6 text-center">
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
            Warranty
          </p>
          <h1 className="mt-6 font-serif text-5xl md:text-7xl leading-[0.95] tracking-tight">
            Designed to be used.
          </h1>
          <p className="mt-8 max-w-2xl mx-auto text-lg text-muted-foreground leading-relaxed">
            Every Double Lion instrument is covered by a five-year international warranty against manufacturing defects. Registration is automatic with every purchase — your serial number is your record.
          </p>
          <div className="mt-12 flex items-center justify-center gap-4">
            <Link
              to="/warranty/claim"
              className="inline-flex items-center justify-center px-8 py-4 bg-foreground text-background font-mono text-[11px] uppercase tracking-[0.25em] hover:bg-foreground/90 transition-colors"
            >
              Submit a claim
            </Link>
          </div>
        </section>

        {/* Coverage */}
        <section className="mt-32 px-6">
          <div className="mx-auto max-w-5xl grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-6">
                01 — What's covered
              </p>
              <ul className="space-y-4">
                {covered.map((item) => (
                  <li key={item} className="flex gap-4 text-base leading-relaxed border-t border-border/60 pt-4">
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground pt-1">✓</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-6">
                02 — What's not covered
              </p>
              <ul className="space-y-4">
                {notCovered.map((item) => (
                  <li key={item} className="flex gap-4 text-base leading-relaxed border-t border-border/60 pt-4 text-muted-foreground">
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] pt-1">—</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Process */}
        <section className="mt-32 px-6">
          <div className="mx-auto max-w-5xl">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-10 text-center">
              03 — The process
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-12">
              {steps.map((s) => (
                <div key={s.n}>
                  <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">{s.n}</div>
                  <h3 className="mt-4 font-serif text-2xl leading-tight">{s.title}</h3>
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{s.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="mt-32 px-6">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="font-serif text-3xl md:text-4xl leading-tight">
              Have an issue with your binoculars?
            </h2>
            <p className="mt-4 text-muted-foreground">
              File a warranty claim and our service team will be in touch within three business days.
            </p>
            <div className="mt-8">
              <Link
                to="/warranty/claim"
                className="inline-flex items-center justify-center px-8 py-4 bg-foreground text-background font-mono text-[11px] uppercase tracking-[0.25em] hover:bg-foreground/90 transition-colors"
              >
                Start a warranty claim
              </Link>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
